import { useEffect, useState } from "react";
import { api, resolveMediaUrl } from "@/lib/api";

/**
 * Fetches mod_settings.logo_url from /api/mod-info and applies it to:
 *   - the browser favicon (removes any conflicting <link rel="icon"> first)
 *   - returns the resolved absolute URL so callers can render an <img> tag
 *
 * Used by Header (public site) AND admin pages so the brand stays consistent
 * across all surfaces. Falls back to /favicon.ico when no logo is configured.
 */
export default function useBrand() {
    const [logoUrl, setLogoUrl] = useState("");
    const [siteTitle, setSiteTitle] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                if (cancelled) return;
                setLogoUrl(data?.logo_url || "");
                setSiteTitle(data?.seo_site_title || "");
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Apply favicon imperatively. We remove ALL existing <link rel="icon">
    // tags (including sized PWA ones from index.html) so the browser uses the
    // dynamic one. Adds a single icon + shortcut icon + apple-touch-icon
    // pointing to logoUrl (or /favicon.ico fallback).
    useEffect(() => {
        try {
            document
                .querySelectorAll(
                    "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
                )
                .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
            const target = logoUrl ? resolveMediaUrl(logoUrl) : "/favicon.ico";
            // iOS PWA "Add to Home Screen" reads apple-touch-icon at INSTALL
            // time. Point it at the dynamic backend endpoint so the OS always
            // captures the latest uploaded logo, regardless of whether this
            // hook has run yet on first paint.
            const appleTarget = "/api/apple-touch-icon.png";
            if (!target) return;
            const link = document.createElement("link");
            link.rel = "icon";
            link.href = target;
            document.head.appendChild(link);
            const shortcut = document.createElement("link");
            shortcut.rel = "shortcut icon";
            shortcut.href = target;
            document.head.appendChild(shortcut);
            // iOS PWA "Add to Home Screen" reads apple-touch-icon, NOT manifest
            const apple = document.createElement("link");
            apple.rel = "apple-touch-icon";
            apple.href = appleTarget;
            document.head.appendChild(apple);
        } catch {
            /* ignore */
        }
    }, [logoUrl]);

    return {
        logoUrl,
        logoSrc: logoUrl ? resolveMediaUrl(logoUrl) : "",
        siteTitle,
    };
}
